import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { Quote, Star, Building2, Briefcase, Globe, Zap } from 'lucide-react';

export function ClientsSection() {
  const clients = [
    { name: 'PT.Cipta Mandiri Wirasakti ', logo: 'CMW', industry: 'Manufacturing', color: 'primary' },
    { name: 'PT.Subang Autocomp Indonesia', logo: 'SUAI', industry: 'Manufacturing', color: 'secondary' },
    { name: 'PT.Sumber Energi Pangan', logo: 'SEP', industry: 'Food', color: 'accent' },
    { name: 'PT.Asahi Best Base Indonesia', logo: 'ABBI', industry: 'Manufacturing', color: 'primary' },
    { name: 'PT.Magnolium Mandiri Indonesia', logo: 'MMI', industry: 'Ingredients', color: 'secondary' },
    // { name: 'SecureNet', logo: 'SN', industry: 'Security', color: 'accent' },
    // { name: 'SmartSolutions', logo: 'SS', industry: 'IoT', color: 'primary' },
    // { name: 'NextGenAI', logo: 'NG', industry: 'Artificial Intelligence', color: 'secondary' }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'CTO, GlobalTech',
      content: 'TechCorp transformed our entire infrastructure. Their AI solutions increased our operational efficiency by 300%. Truly remarkable work.',
      rating: 5,
      avatar: 'SC'
    },
    {
      name: 'Marcus Rodriguez',
      role: 'CEO, InnovateCorp',
      content: 'The mobile app they developed became our primary revenue driver. Their attention to detail and innovative approach exceeded all expectations.',
      rating: 5,
      avatar: 'MR'
    },
    {
      name: 'Elena Vasquez',
      role: 'Director, FutureVision',
      content: 'Working with TechCorp was seamless. They delivered a complex project ahead of schedule while maintaining the highest quality standards.',
      rating: 5,
      avatar: 'EV'
    }
  ];

  const stats = [
    { icon: Building2, label: 'Enterprise Clients', value: '150+' },
    { icon: Briefcase, label: 'Successful Projects', value: '500+' },
    { icon: Globe, label: 'Countries Served', value: '25+' },
    { icon: Zap, label: 'Client Satisfaction', value: '98%' }
  ];

  return (
    <section id="clients" className="py-20 relative bg-muted/5">
      <div className="absolute inset-0 digital-grid opacity-10"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-primary/20 text-primary border-primary/30 font-orbitron">
            Our Clients
          </Badge>
          <h2 className="text-4xl md:text-5xl font-orbitron font-bold text-primary mb-6">
            Trusted by Industry Leaders
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-primary to-accent mx-auto mb-8"></div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From startups to Fortune 500 companies, our clients trust us to deliver 
            innovative solutions that drive real results.
          </p>
        </div>
        
        {/* Client Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="p-6 text-center bg-card/30 backdrop-blur border-primary/20 hover:border-primary/40 transition-all duration-300 hover:tech-glow">
                <Icon className="w-8 h-8 text-primary mx-auto mb-4" />
                <div className="text-2xl md:text-3xl font-orbitron font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground">
                  {stat.label}
                </div>
              </Card>
            );
          })}
        </div>
        
        {/* Client Logos */}
        <div className="mb-16">
          <h3 className="text-2xl font-orbitron font-semibold text-center text-primary mb-8">
            Partnering with Innovation Leaders
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6">
            {clients.map((client, index) => (
              <Card 
                key={index} 
                className={`flex flex-col justify-between h-full p-6 text-center bg-card/20 backdrop-blur border-${client.color}/20 hover:border-${client.color}/40 transition-all duration-300 hover:tech-glow hover:-translate-y-1`}
              >
                <div className={`w-12 h-12 bg-${client.color}/20 rounded-xl flex items-center justify-center mx-auto mb-3`}>
                  <span className={`font-orbitron font-bold text-${client.color}`}>
                    {client.logo}
                  </span>
                </div>
                <div className="text-sm font-medium text-foreground mb-1">
                  {client.name}
                </div>
                <div className="text-xs text-muted-foreground">
                  {client.industry}
                </div>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Testimonials */}
        <div className="mb-16">
          <h3 className="text-2xl font-orbitron font-semibold text-center text-primary mb-8">
            What Our Clients Say
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="p-8 bg-card/30 backdrop-blur border-primary/20 hover:border-primary/40 transition-all duration-300 hover:tech-glow">
                <Quote className="w-8 h-8 text-primary mb-4" />
                
                <p className="text-muted-foreground mb-6 leading-relaxed italic">
                  "{testimonial.content}"
                </p>
                
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-primary fill-current" />
                  ))}
                </div>
                
                <div className="flex items-center space-x-3">
                  <Avatar className="w-10 h-10 border-2 border-primary/20">
                    <AvatarFallback className="bg-primary/10 text-primary font-orbitron">
                      {testimonial.avatar}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium text-foreground">{testimonial.name}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Success Story Highlight */}
        <Card className="p-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-orbitron font-bold text-primary mb-4">
                Success Story: GlobalTech Transformation
              </h3>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                We helped GlobalTech modernize their legacy systems with cutting-edge AI solutions, 
                resulting in a 300% increase in operational efficiency and $2M in cost savings annually.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-orbitron font-bold text-primary">300%</div>
                  <div className="text-sm text-muted-foreground">Efficiency Increase</div>
                </div>
                <div>
                  <div className="text-2xl font-orbitron font-bold text-secondary">$2M</div>
                  <div className="text-sm text-muted-foreground">Annual Savings</div>
                </div>
              </div>
            </div>
            <div className="relative">
              {/* <ImageWithFallback
                src="https://images.unsplash.com/photo-1695891583421-3cbbf1c2e3bd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBidWlsZGluZ3xlbnwxfHx8fDE3NTQ0NDc0MTl8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Corporate success"
                className="w-full h-64 object-cover rounded-xl tech-glow"
              /> */}
              {/* Gambar */}
              <ImageWithFallback
                src="./team2.jpg"
                alt="Corporate success"
                className="w-full h-64 object-cover rounded-xl tech-glow"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent rounded-xl"></div>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
}